import moment from "moment";
import { capitalizeAllLetters } from "Utils/utils";
import { buildAiStatusFromRow } from "../../TimeSheetDetailsSection/ai/utils/timesheetAiUtils";

export const BULK_PAGE_SIZE = 10;
export const BULK_POLL_MS = 20000;
/** Rough wall-clock per row: chunks of 5 run in parallel, ~10–15s per chunk. */
export const BULK_SECONDS_PER_ROW = 3;

/** Row status as stored by the backend (ai_timesheet_bulk_job_items.status). */
export const RUN_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  SAVED: "done",
  FAILED: "failed",
  LEFT_OUT: "left_out",
  STOPPED: "stopped",
};

export const JOB_STATUS = {
  RUNNING: "running",
  FINISHED: "finished",
  STOPPED: "stopped",
};

export const STOP_REASON_LABEL = {
  user_stopped: "Paused",
  credits_exhausted: "Paused: AI credits ran out",
  feature_disabled: "Paused: bulk analysis was turned off",
  service_auth_failed: "Paused: AI service could not be reached",
  high_failure_rate: "Paused: AI service had too many errors",
  tenant_suspended: "Paused: account is not active",
};

export const stopReasonLabel = (reason) =>
  (reason && STOP_REASON_LABEL[reason]) || "Paused";

const num = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/** Counts for the status bar and job modal, from the run payload. */
export function countRun(job) {
  if (!job) {
    return { done: 0, failed: 0, running: 0, queued: 0, stopped: 0, leftOut: 0, remaining: 0, total: 0 };
  }
  const done = num(job.done);
  const failed = num(job.failed);
  const leftOut = num(job.left_out);
  const running = num(job.running);
  const queued = num(job.queued);
  const stopped = num(job.stopped);
  const total = Math.max(0, num(job.total) - leftOut);
  const waiting = queued + running + stopped;
  const remaining =
    job.status === JOB_STATUS.RUNNING
      ? Math.max(0, job.remaining != null ? num(job.remaining) : waiting)
      : waiting;
  return { done, failed, running, queued, stopped, leftOut, remaining, total };
}

const isSettled = (job) => Boolean(job) && job.status !== JOB_STATUS.RUNNING;

/** Timesheets parked because the run was paused. */
export function pausedCount(job) {
  if (!isSettled(job)) return 0;
  return countRun(job).stopped;
}

/** Timesheets the AI did not finish. Retry is only for these. */
export function failedRetryCount(job) {
  if (!isSettled(job)) return 0;
  return countRun(job).failed;
}

export const retryCount = failedRetryCount;

export function canResume(job) {
  return pausedCount(job) > 0;
}

export function canRetry(job) {
  return failedRetryCount(job) > 0;
}

export function percentOf(counts) {
  if (!counts.total) return 0;
  return Math.min(100, Math.round(((counts.done + counts.failed) / counts.total) * 100));
}

/** Guess only: about 3 seconds for each timesheet still waiting. Not a clock. */
export function formatEta(remaining) {
  return formatEtaCountdown(remaining, 0);
}

/** Same estimate, reduced by seconds already waited since the last count change. */
export function formatEtaCountdown(remaining, elapsedSeconds = 0) {
  if (!remaining) return "Completed";
  const secondsLeft = remaining * BULK_SECONDS_PER_ROW - Math.max(0, Math.floor(elapsedSeconds));
  if (secondsLeft <= 0) return "Finishing…";
  return `~${formatRunDuration(secondsLeft)} left`;
}

/**
 * Elapsed time for a run. Only the parts that exist are included:
 * 8 sec, 2 min, 2 min 8 sec, 1 hr, 1 hr 2 min, 1 hr 2 min 8 sec.
 */
export function formatRunDuration(totalSeconds) {
  if (totalSeconds == null || !Number.isFinite(Number(totalSeconds))) return "—";
  const total = Math.max(0, Math.floor(Number(totalSeconds)));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  const parts = [];
  if (hours > 0) parts.push(`${hours} hr`);
  if (minutes > 0) parts.push(`${minutes} min`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds} sec`);
  return parts.join(" ");
}

export function runElapsedSeconds(job, nowMs = Date.now()) {
  const start = Date.parse(job?.created_at || "");
  if (!Number.isFinite(start)) return null;
  const running = job?.status === JOB_STATUS.RUNNING;
  const end = running ? nowMs : Date.parse(job?.finished_at || job?.stopped_at || "");
  const endMs = Number.isFinite(end) ? end : nowMs;
  const pausedSeconds = Math.max(0, Number(job?.paused_seconds) || 0);
  return Math.max(0, Math.floor((endMs - start) / 1000) - pausedSeconds);
}

/** Dollar cost of one run. Small amounts keep four places so $0.1954 is not shown as $0.20. */
export function formatRunCost(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "—";
  if (amount === 0 || Math.abs(amount) >= 1) return `$${amount.toFixed(2)}`;
  return `$${amount.toFixed(4)}`;
}

/**
 * Accept a run update only if it is not older than what we hold. Pusher,
 * the fallback poll and request responses can arrive in any order.
 */
export function mergeBulkJob(current, incoming) {
  if (!incoming) return current || null;
  if (!current || current.uuid !== incoming.uuid) return incoming;
  if (num(incoming.version) < num(current.version)) return current;
  return { ...current, ...incoming };
}

/** Pusher carries counts only; fold them into the run we already hold. */
export function jobFromPusher(message, current) {
  if (!message?.job_uuid) return null;
  const partial = {
    uuid: message.job_uuid,
    status: message.job_status,
    stop_reason: message.stop_reason ?? null,
    total: num(message.total),
    done: num(message.done),
    failed: num(message.failed),
    left_out: num(message.left_out),
    version: num(message.version),
  };
  if (message.running != null) partial.running = num(message.running);
  if (message.queued != null) partial.queued = num(message.queued);
  if (current && current.uuid === partial.uuid) {
    const stopped = num(current.stopped);
    partial.remaining =
      partial.status === JOB_STATUS.RUNNING
        ? Math.max(0, partial.total - partial.left_out - partial.done - partial.failed - stopped)
        : 0;
  }
  return partial;
}

export const isFinalStatus = (status) =>
  status === JOB_STATUS.FINISHED || status === JOB_STATUS.STOPPED;

const parseClock = (value) => {
  if (!value) return null;
  const parsed = moment(value, ["YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", moment.ISO_8601], true);
  if (parsed.isValid()) return parsed;
  const fallback = moment(value);
  return fallback.isValid() ? fallback : null;
};

/** Shift date + scheduled times, same shape as the timesheet list. */
export function shiftDisplay(row) {
  const day = row?.shift_date ? moment(row.shift_date) : null;
  const clock = (value) => {
    const parsed = parseClock(value);
    return parsed ? parsed.format("h:mm A") : "—";
  };
  return {
    day: day?.isValid() ? day.format("dddd") : "—",
    date: day?.isValid() ? day.format("DD/MM/YY") : "—",
    scheduleStart: clock(row?.scheduled_start),
    scheduleEnd: clock(row?.scheduled_end),
  };
}

function personName(person) {
  return capitalizeAllLetters(
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim()
  );
}

function peopleFields(row) {
  const participants = Array.isArray(row.participants) ? row.participants : [];
  const participant = participants.map(personName).filter(Boolean).join(", ");
  return {
    worker: personName(row.worker) || "—",
    workerProfile: row.worker || null,
    participant: participant || "—",
    participants,
  };
}

/** Preview row (confirm modal) → table row. */
export function previewRowToTableRow(row) {
  return {
    id: `ts-${row.timesheet_id}`,
    timesheetId: row.timesheet_id,
    ...peopleFields(row),
    shift: shiftDisplay(row),
    timesheetStatus: capitalise(row.timesheet_status),
    aiStatus: buildAiStatusFromRow(row.ai_anomaly),
    runStatus: null,
  };
}

/** Run item (job modal) → table row. */
export function itemToTableRow(item) {
  return {
    id: `item-${item.id}`,
    timesheetId: item.timesheet_id,
    ...peopleFields(item),
    shift: shiftDisplay(item),
    timesheetStatus: capitalise(item.timesheet_status),
    aiStatus: buildAiStatusFromRow(item.ai_anomaly),
    runStatus: item.status,
    errorMessage: item.error_message || "",
  };
}

function capitalise(value) {
  const text = String(value || "");
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : "—";
}

export function pageInfo(pagination, rowsLength = 0) {
  const total = num(pagination?.total);
  const page = Math.max(1, num(pagination?.current_page) || 1);
  const totalPages = Math.max(1, num(pagination?.last_page) || Math.ceil(total / BULK_PAGE_SIZE) || 1);
  return { page, total, totalPages, pageSize: BULK_PAGE_SIZE, rowsLength };
}

/** Date presets relative to today; the backend caps a run at 93 days. */
export function buildDatePresets(today = moment(), pageRange = null) {
  const end = moment(today).startOf("day");
  const make = (id, label, days) => ({
    id,
    label,
    from: moment(end).subtract(days - 1, "days").format("YYYY-MM-DD"),
    to: end.format("YYYY-MM-DD"),
  });
  const presets = [];
  if (pageRange?.from && pageRange?.to) {
    presets.push({
      id: "page",
      label: formatRangeLabel(pageRange.from, pageRange.to),
      from: moment(pageRange.from).format("YYYY-MM-DD"),
      to: moment(pageRange.to).format("YYYY-MM-DD"),
    });
  }
  presets.push(make("last7", "Last 7 days", 7));
  presets.push(make("last14", "Last 14 days", 14));
  presets.push(make("last30", "Last 30 days", 30));
  return presets;
}

export function formatRangeLabel(from, to) {
  const a = moment(from);
  const b = moment(to);
  if (!a.isValid() || !b.isValid()) return "";
  return `${a.format("D MMM")} – ${b.format("D MMM")}`;
}

export function defaultBulkFilters(presets) {
  const preset = presets?.[0];
  return {
    worker: "all",
    participant: "all",
    presetId: preset?.id || "",
    from: preset?.from || "",
    to: preset?.to || "",
    leaveOutAlreadyAnalysed: true,
  };
}

/** Confirm-modal filters → API body for preview and create. */
export function buildRunPayload(filters, extra = {}) {
  const ids = (value) => (value && value !== "all" ? [Number(value)] : "all");
  return {
    filters: {
      timesheet_date_range: [filters.from, filters.to],
      worker_id: ids(filters.worker),
      pt_id: ids(filters.participant),
    },
    leave_out_already_analysed: Boolean(filters.leaveOutAlreadyAnalysed),
    ...extra,
  };
}

export function newIdempotencyKey() {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `bulk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function runDateLabel(job) {
  const range = job?.filters?.timesheet_date_range;
  if (!Array.isArray(range) || range.length !== 2) return "";
  return formatRangeLabel(range[0], range[1]);
}
